/**
 * AppNavbar — shared sticky top bar used across all app shells
 * (Platform, Admin, and any future shell).
 *
 * Renders: mobile hamburger | shell actions | help | notifications | profile.
 * All sections are self-contained — no shell-specific logic lives here.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAuth, useFeatureEntitlements } from "@frontegg/react";
import { useQuery } from "@apollo/client";
import { Badge, Box } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  HelpCircleIcon,
  Menu01Icon,
  Notification01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { GET_USER } from "../../graphql/user";
import NotificationPopover from "components/common/notifications/NotificationPopover";
import UserAvatar from "../common/users/UserAvatar";
import { useAppEntitlements } from "hooks/useAppEntitlements";
import { useBusinessCardholders } from "hooks/useBusinessCardholders";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { missingCardholderDetails } from "utils/users/cardholderDetails";
import UserProfileDrawer from "components/common/users/UserProfileDrawer";
import { Inline } from "components/layout/reusableLayouts";
import { useLogoForScheme } from "utils/themeContextHelper";
import { appKeyForRoute } from "utils/apps";
import AppProfileMenu from "./AppProfileMenu";
import { ConfirmDialog, IconButton } from "@outpave/ds";

const PROMPT_SEEN_KEY = "profilePromptSeenSession";

const promptSeenThisSession = (session?: string): boolean => {
  try {
    return !!session && localStorage.getItem(PROMPT_SEEN_KEY) === session;
  } catch {
    return false;
  }
};

const markPromptSeen = (session?: string) => {
  try {
    if (session) localStorage.setItem(PROMPT_SEEN_KEY, session);
  } catch {
    // Without storage the prompt simply returns on the next page.
  }
};

export interface AppNavbarProps {
  handleCollapsed: () => void;
  isSmallScreen: boolean;
  bg?: string;
  /**
   * Optional shell-specific action(s) rendered in the right section,
   * before the Help button. Used by the Employee shell for the
   * "Back to admin" pill.
   */
  extraActions?: React.ReactNode;
  /**
   * Optional shell-specific action(s) rendered in the left section, after the
   * mobile menu. Used by the Admin shell for the dashboard's create shortcuts.
   */
  leadingActions?: React.ReactNode;
  /**
   * The account menu this shell opens from the company avatar. Each shell has
   * its own identity, items and drawers, so the bar owns the trigger and the
   * anchor while the shell owns the menu.
   * @default AppProfileMenu
   */
  ProfilePopoverComponent?: React.ComponentType<{
    id?: string;
    open: boolean;
    anchorEl: HTMLElement | null;
    handleClose: () => void;
    userData?: unknown;
  }>;
}

const AppNavbar: React.FC<AppNavbarProps> = ({
  handleCollapsed,
  isSmallScreen,
  bg = "var(--color-bg-surface)",
  extraActions,
  leadingActions,
  ProfilePopoverComponent = AppProfileMenu,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logo = useLogoForScheme();
  const { isEntitled: featureNotification } = useFeatureEntitlements(
    "feature_notifications",
  );

  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [almostTotalNotifications, setAlmostTotalNotifications] = useState(0);
  const [showUserUpdateModal, setShowUserUpdateModal] = useState(false);

  const profileOpen = Boolean(profileAnchorEl);
  const profileId = profileOpen ? "app-profile-popover" : undefined;

  const { data: userData, loading: loadingUser } = useQuery(GET_USER, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });
  const location = useLocation();

  const { spend } = useAppEntitlements();
  const businessCardholders = useBusinessCardholders();
  const profileDrawer = useDrawerRecord<string>();
  // One nudge per sign-in: the session id changes when the person signs in again.
  const session = user?.sid ?? user?.auth_time?.toString();

  useEffect(() => {
    if (loadingUser || !userData?.user) return;
    const onSpendRoute =
      location.pathname.startsWith("/my") ||
      appKeyForRoute(location.pathname) === "spend";
    setShowUserUpdateModal(
      onSpendRoute &&
        spend &&
        !businessCardholders &&
        missingCardholderDetails(userData.user) &&
        !promptSeenThisSession(session),
    );
  }, [loadingUser, userData, location, spend, businessCardholders, session]);

  const closePrompt = () => {
    markPromptSeen(session);
    setShowUserUpdateModal(false);
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: bg,
        borderRadius: "var(--radius-2xl) 0 0 0",
      }}
    >
      <Inline
        component="header"
        gap="var(--space-5)"
        wrap={false}
        justify="between"
        sx={{
          padding: "var(--space-5)",
          paddingInline: "var(--space-7)",
          width: "100%",
          maxWidth: "var(--layout-content-width)",
        }}
      >
        {/* Left: mobile menu / logo (tablet+) / shell actions */}
        <Inline
          gap="var(--space-7)"
          wrap={false}
          sx={{ flex: "1 1 auto", minWidth: 0 }}
        >
          {isSmallScreen && (
            <IconButton
              aria-label={t("Open menu")}
              color="default"
              variant="outlined"
              size="medium"
              onClick={handleCollapsed}
            >
              <HugeiconsIcon icon={Menu01Icon} size={20} strokeWidth={2} />
            </IconButton>
          )}
          {isSmallScreen && logo && (
            <Box
              component="img"
              src={logo}
              alt={t("Company Logo")}
              sx={{
                height: 28,
                width: "auto",
                minWidth: 0,
                display: { xs: "none", sm: "block" },
                flexShrink: 1,
              }}
            />
          )}
          {leadingActions}
        </Inline>

        {/* Right: help / notifications / profile */}
        <Inline gap="var(--space-3)" wrap={false} sx={{ flexShrink: 0 }}>
          {extraActions && <Box sx={{ flexShrink: 0 }}>{extraActions}</Box>}

          <IconButton
            aria-label={t("Help")}
            color="default"
            variant="outlined"
            size="medium"
            onClick={() => window.Atlas?.chat?.openWindow?.()}
          >
            <HugeiconsIcon icon={HelpCircleIcon} size={20} />
          </IconButton>

          {featureNotification && (
            <NotificationPopover
              setAlmostTotalNotifications={setAlmostTotalNotifications}
              trigger={({ toggle }) => (
                <Badge
                  overlap="circular"
                  variant="dot"
                  color="primary"
                  invisible={!almostTotalNotifications}
                >
                  <IconButton
                    aria-label={t("Notifications")}
                    color="default"
                    variant="outlined"
                    size="medium"
                    onClick={toggle}
                  >
                    <HugeiconsIcon icon={Notification01Icon} size={20} />
                  </IconButton>
                </Badge>
              )}
            />
          )}

          {!loadingUser && (
            <IconButton
              id={profileId}
              aria-label={t("Profile menu")}
              color="default"
              variant="outlined"
              size="medium"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                setProfileAnchorEl(e.currentTarget)
              }
            >
              <UserAvatar user={userData?.user} size="lg" />
            </IconButton>
          )}
        </Inline>
      </Inline>

      <ProfilePopoverComponent
        open={profileOpen}
        anchorEl={profileAnchorEl}
        id={profileId}
        handleClose={() => setProfileAnchorEl(null)}
        userData={userData}
      />
      <ConfirmDialog
        open={showUserUpdateModal}
        tone="primary"
        title={t("User Updates Required")}
        message={t(
          "Your user profile is missing some required information, please update your profile",
        )}
        confirmLabel={t("Update Profile")}
        cancelLabel={t("Later")}
        onConfirm={() => {
          closePrompt();
          if (userData?.user?.id) profileDrawer.show(userData.user.id);
        }}
        onCancel={closePrompt}
      />
      {profileDrawer.record && (
        <UserProfileDrawer
          open={profileDrawer.open}
          edit
          selectedUserId={profileDrawer.record}
          handleCloseDrawer={profileDrawer.close}
          onAfterClose={profileDrawer.afterClose}
        />
      )}
    </Box>
  );
};

export default AppNavbar;
